import { Fragment, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';

import { sortTableDateFecha } from '../../utils/generalFunctions';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import { buscarArchivos, limpiarArchivos, filtarArchivos, obtenerArchivo } from '../../actions/expediente';

import FiltrosArchivos from './FiltrosArchivos';
import NuevoArchivo from './NuevoArchivo';
import EditarArchivo from './EditarArchivo';
import EliminarArchivo from './EliminarArchivo';

const Archivos = () => {

    let rutasBread = [
        {
            activo: true,
            nombre: 'Expediente',
            path: '/expediente'
        }
    ];

    const dispatch =  useDispatch();

    const { usuario } = useSelector(state => state.auth);
    const { listado, detalles } = useSelector(state => state.expediente);

    const [archivos, setArchivos] = useState([]);

    /* Archivos */
    const [nuevoArchivo, setNuevoArchivo] = useState(null);
    const [editarArchivo, setEditarArchivo] = useState(null);
    const [eliminarArchivo, setEliminarArchivo] = useState(null);

    const paginationOptions = { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' };

    const conditionalRowStyles = [
        {
            when: row => row.precargado,
            style: { backgroundColor: '#fffbeb', borderLeft: '3px solid #f59e0b' }
        }
    ];

    const columns = [
        {
            name: 'Nombre',
            sortable: true,
            grow: 2,
            cell: row => (
                <span>
                    {row.nombre || 'Sin definir'}
                    {row.precargado && (
                        <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.7rem' }}>Pendiente</span>
                    )}
                </span>
            )
        },
        {
            name: 'Descripción',
            sortable: true,
            grow: 2,
            selector: row => row.descripcion || 'Sin definir'
        },
        {
            name: 'Tipo',
            sortable: true,
            selector: row => row.precargado ? '—' : (row.extension ? row.extension.toUpperCase() : 'Sin definir')
        },
        {
            name: 'Fecha de carga',
            sortable: true,
            sortFunction: sortTableDateFecha,
            cell: item => item.precargado
                ? <span className="text-muted" style={{ fontSize: '0.85rem' }}>Sin archivo</span>
                : (item.fecha ? new Date(item.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Sin definir')
        },
        {
            name: '',
            grow: 1,
            cell: row => (
                <div className="text-right w-100">
                    {!row.precargado && usuario?.permisos?.expediente?.eliminar && (
                        <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleEliminarArchivo({ id_archivo: row._id, nombre: row.nombre })}>
                            <i className="fas fa-times text-danger"></i>
                        </button>
                    )}
                    {usuario?.permisos?.expediente?.editar && (
                        row.precargado
                            ? <button type="button" className="btn btn-sm btn-warning px-2 float-end" onClick={() => handleEditarArchivo({ id_archivo: row._id, ...row })} title="Subir archivo">
                                <i className="fas fa-upload me-1"></i> Subir
                              </button>
                            : <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleEditarArchivo({ id_archivo: row._id, ...row })}>
                                <i className="fas fa-edit text-primary"></i>
                              </button>
                    )}
                    {!row.precargado && usuario?.permisos?.expediente?.detalles && (
                        <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleDetallesArchivo({ id_archivo: row._id })}>
                            <i className="fas fa-eye text-info"></i>
                        </button>
                    )}
                </div>
            )
        }
    ];

    /* Filtros de busqueda */
    const handleFiltrosBusqueda = () => {
        dispatch(filtarArchivos(true));
    }

    /* Archivos */
    const handleAgregarArchivo = (archivo) => {
        setNuevoArchivo(archivo);
    }

    const handleDetallesArchivo = (archivo) => {
        dispatch(obtenerArchivo(archivo));
    }

    const handleAbrirArchivo = () => {
        if(!editarArchivo){
            window.open(detalles.url);
        }
    }

    const handleEditarArchivo = (cliente) => {
        setEditarArchivo(cliente);
    }

    const handleEliminarArchivo = (cliente) => {
        setEliminarArchivo(cliente);
    }

    useEffect(() => {
        dispatch(buscarArchivos({
            fecha_inicio: null, 
            fecha_fin: null, 
            nombre: null
        }));

        return () => {
            dispatch(limpiarArchivos());
        }
    }, []);

    useEffect(() => {
        // Precargados al tope, luego por fecha descendente
        const sorted = [...(listado || [])].sort((a, b) => {
            if(a.precargado && !b.precargado) return -1;
            if(!a.precargado && b.precargado) return 1;
            return new Date(b.fecha) - new Date(a.fecha);
        });
        setArchivos(sorted);
    }, [listado]);

    useEffect(() => {        
        if(detalles && detalles?.extension && detalles?.url){
            handleAbrirArchivo();
        }
    }, [detalles]);

    useEffect(() => {
        dispatch(editarBreadcrumb(rutasBread));
    }, [rutasBread]);

    return (
        <Fragment>

            <FiltrosArchivos/>
            <NuevoArchivo archivo={nuevoArchivo} reestablecerArchivo={handleAgregarArchivo} />
            <EditarArchivo archivo={editarArchivo} reestablecerArchivo={handleEditarArchivo} />
            <EliminarArchivo archivo={eliminarArchivo} reestablecerArchivo={handleEliminarArchivo} />

            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="title-section my-5">Expediente</h3>

                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn btn-primary me-2" onClick={() => handleFiltrosBusqueda()}>Filtros de búsqueda</button>

                        <button type="button" className="btn btn-primary" onClick={() => handleAgregarArchivo({})}>Nuevo archivo</button>
                    </div>

                    <div className="mt-3">
                        <DataTable
                            columns={columns}
                            data={archivos}
                            noDataComponent="No hay archivos registrados"
                            pagination
                            paginationPerPage={20}
                            paginationRowsPerPageOptions={[5, 10, 20, 50]}
                            paginationComponentOptions={paginationOptions}
                            highlightOnHover={true}
                            conditionalRowStyles={conditionalRowStyles}
                        />
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default Archivos;